import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { CardItem, ObjectiveKpiCard, type ObjectiveKpiFooterTag } from '@bloomays-lib/ui.shared';
import { useCLevelAnnualSynthesis } from '../../hooks/useCLevelAnnualSynthesis';
import { useCLevelSynthesisKpis } from '../../hooks/useCLevelSynthesisKpis';
import type { ActiveTam, CLevelSynthesisRow, LifetimeMarginCumulAggregate } from '../../types/primes';
import { formatPrimeEuro } from '../../utils/monthlyPrime.utils';

const KPI_LOADING_PLACEHOLDER = '…';

export type CLevelSynthesisKpisBandProps = {
  activeTams: ActiveTam[];
  synthesisRows: CLevelSynthesisRow[];
  selectedYear: string;
  lifetimeMarginCumuls: LifetimeMarginCumulAggregate[];
  lifetimeLoading: boolean;
  lifetimeMarginCumulsFailed: boolean;
  /** Permet aux stories/tests d'injecter l'année N-1 sans requête Apollo. */
  previousYearRows?: CLevelSynthesisRow[];
};

const CLevelSynthesisKpisBand = ({
  activeTams,
  synthesisRows,
  selectedYear,
  lifetimeMarginCumuls,
  lifetimeLoading,
  lifetimeMarginCumulsFailed,
  previousYearRows: previousYearRowsOverride,
}: CLevelSynthesisKpisBandProps) => {
  const { t } = useTranslation(['primes']);
  const previousYear = String(Number(selectedYear) - 1);
  const synthesisReadyForEvolution =
    activeTams.length > 0 && synthesisRows.length > 0 && !synthesisRows.some((row) => row.rowStatus === 'loading');
  const shouldFetchPreviousYear = synthesisReadyForEvolution && previousYearRowsOverride === undefined;
  const previousYearRowsFromHook = useCLevelAnnualSynthesis(shouldFetchPreviousYear ? activeTams : [], previousYear);
  const previousYearRows = previousYearRowsOverride ?? previousYearRowsFromHook;
  const synthesisKpis = useCLevelSynthesisKpis(synthesisRows, selectedYear, previousYearRows, lifetimeMarginCumuls);

  const { marginGenerated, primesCalculated, lifetimeMarginTotal } = synthesisKpis;
  const synthesisKpisLoading = synthesisKpis.synthesisLoading;
  const marginEvolutionLoading = synthesisKpis.marginEvolutionLoading;
  const marginEvolutionPercent =
    marginEvolutionLoading || synthesisKpisLoading ? null : marginGenerated.evolutionPercent;
  const marginGeneratedFooter = t('clevelSynthesisKpiMarginGeneratedFooter');
  const marginEvolutionTag: ObjectiveKpiFooterTag | undefined =
    marginEvolutionPercent === null
      ? undefined
      : {
          label: t('clevelSynthesisKpiEvolutionSuffix', {
            sign: marginEvolutionPercent >= 0 ? '+' : '',
            percent: marginEvolutionPercent,
          }),
          tone: marginEvolutionPercent < 0 ? 'error' : 'solaire',
        };
  const primesCalculatedFooter = t('clevelSynthesisKpiPrimesCalculatedFooter');
  const primesContributingTamTag: ObjectiveKpiFooterTag | undefined = synthesisKpisLoading
    ? undefined
    : {
        label: t('clevelSynthesisKpiContributingTamBadge', { count: primesCalculated.contributingTamCount }),
        tone: 'error',
      };
  const lifetimeMarginFooter = lifetimeMarginCumulsFailed
    ? t('clevelMarginRankingLoadError')
    : t('clevelSynthesisKpiLifetimeMarginFooter');
  const lifetimeActiveTamTag: ObjectiveKpiFooterTag | undefined =
    lifetimeMarginCumulsFailed || lifetimeLoading
      ? undefined
      : {
          label: t('clevelSynthesisKpiActiveTamBadge', { count: lifetimeMarginTotal.activeTamCount }),
          tone: 'info',
        };

  return (
    <KpiRow>
      <ObjectiveKpiCard
        data-testid="kpi-margin-generated"
        accent="green"
        icon={<CardItem variant="green" size="small" emoji={<TrendingUpOutlinedIcon fontSize="small" />} />}
        title={t('clevelSynthesisKpiMarginGeneratedTitle', { year: selectedYear })}
        valuePrimary={synthesisKpisLoading ? KPI_LOADING_PLACEHOLDER : formatPrimeEuro(marginGenerated.amount)}
        progressPercent={marginGenerated.progressPercent}
        footer={marginGeneratedFooter}
        footerTag={marginEvolutionTag}
      />
      <ObjectiveKpiCard
        data-testid="kpi-primes-calculated"
        accent="pink"
        icon={<CardItem variant="pink" size="small" emoji={<PaidOutlinedIcon fontSize="small" />} />}
        title={t('clevelSynthesisKpiPrimesCalculatedTitle', { year: selectedYear })}
        valuePrimary={synthesisKpisLoading ? KPI_LOADING_PLACEHOLDER : formatPrimeEuro(primesCalculated.amount)}
        progressPercent={marginGenerated.progressPercent}
        footer={primesCalculatedFooter}
        footerTag={primesContributingTamTag}
      />
      <ObjectiveKpiCard
        data-testid="kpi-lifetime-margin-total"
        accent="blue"
        icon={<CardItem variant="blue" size="small" emoji={<StackedLineChartOutlinedIcon fontSize="small" />} />}
        title={t('clevelSynthesisKpiLifetimeMarginTitle')}
        valuePrimary={
          lifetimeMarginCumulsFailed
            ? '—'
            : lifetimeLoading
            ? KPI_LOADING_PLACEHOLDER
            : formatPrimeEuro(lifetimeMarginTotal.amount)
        }
        progressPercent={0}
        footer={lifetimeMarginFooter}
        footerTag={lifetimeActiveTamTag}
      />
    </KpiRow>
  );
};

export default CLevelSynthesisKpisBand;

const KpiRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));
